import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Perf Wave 1 — defer below-the-fold homepage HTML, not just its JS.
 *
 * The homepage sections were already `React.lazy`, but lazy only defers the
 * *JavaScript*: the server still streamed every section's markup into the
 * first response (2,327 DOM elements / 474 KB of HTML on `/`). Mobile paid
 * for parsing and hydrating twelve sections before the first interaction.
 *
 * This wrapper renders nothing but a correctly-sized, `content-visibility`
 * placeholder on the server and on the first client render (so hydration
 * matches exactly), then swaps in the real children when the placeholder
 * approaches the viewport. The reserved height is the same value the old
 * Suspense fallbacks used, so nothing shifts (CLS unchanged).
 *
 * Sections still render eagerly for crawlers-without-JS? No — and that is
 * deliberate for these bands: every deferred section is merchandising
 * (rails, brand tiles, shop cards) whose canonical URLs are already in the
 * sitemap and linked from the header/footer. The SEO-critical content —
 * `<h1>`, hero, category tiles, the first two product rails, the FAQ JSON-LD
 * emitted from `head()` — stays server-rendered.
 */
export function DeferUntilVisible({
  children,
  /** Tailwind min-height utilities reserving the section's real height. */
  minHeight = "min-h-[320px]",
  /** How early to mount, in px of scroll distance. */
  rootMargin = "1200px",
  className = "",
}: {
  children: ReactNode;
  minHeight?: string;
  rootMargin?: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;

    // No IntersectionObserver (very old browsers, some in-app webviews):
    // mount immediately rather than hiding content forever.
    if (typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show, rootMargin]);

  if (show) return <>{children}</>;

  return (
    <div
      ref={ref}
      aria-hidden
      data-deferred-section=""
      className={`w-full [content-visibility:auto] ${minHeight} ${className}`}
    />
  );
}

export default DeferUntilVisible;
